import type { ServiceKey } from '@shared/api/config';

/**
 * Where an account-role grant is written, and in whose words.
 *
 * **`backend-auth` does not fan the grant out, and says so at its own route:**
 * «a peer service's role name is a `422`; that grant belongs on that service's
 * own identical route, and proxying it here would put its authorization decision
 * in this database». Six peers state the other half in identical words on their
 * own role-grant gate — «the console is the only surface that grants a role, and
 * it reaches each service **directly**… a `SERVICE`-only gate left this route
 * with no caller at all» — and each of them admits this console's own `admin`
 * session for exactly that reason.
 *
 * So the fan-out is the console's, and it was the half nobody had written: a
 * grant made here wrote one row in `auth__user_roles` and nothing anywhere else,
 * which produced an «operator» who held `operator` in one service and the
 * default role in the other eight. With the sidebar now drawn from what each
 * service answers, that account signed in to a console with almost nothing in
 * it — a role that named a job it could not do.
 *
 * **The bodies genuinely differ, and this table is the honest record of it.**
 * Three shapes are in production; `finance`, `support` and `notification`
 * declare `extra="forbid"`, so sending the wrong optional key is a `422` rather
 * than an ignored field.
 */
export type GrantBodyShape = 'role-only' | 'granted-by' | 'granted-by-user-id';

export interface GrantTarget {
  service: ServiceKey;
  body: GrantBodyShape;
  /**
   * The role names this service seeds.
   *
   * A grant naming anything else is **skipped rather than sent**: roles are per
   * service and a service seeds only the roles it distinguishes, so a name it
   * never seeded is not a failure to report — it is a question that does not
   * arise there.
   */
  seeds: readonly string[];
}

const OPERATOR_TIERS = ['user', 'operator', 'admin'] as const;

export const GRANT_TARGETS: readonly GrantTarget[] = [
  { service: 'auth', body: 'role-only', seeds: OPERATOR_TIERS },
  { service: 'content', body: 'granted-by', seeds: OPERATOR_TIERS },
  { service: 'engagement', body: 'granted-by-user-id', seeds: OPERATOR_TIERS },
  { service: 'analytics', body: 'role-only', seeds: OPERATOR_TIERS },
  { service: 'finance', body: 'granted-by-user-id', seeds: OPERATOR_TIERS },
  { service: 'support', body: 'granted-by-user-id', seeds: OPERATOR_TIERS },
  // `notification` seeds no `operator`: the catalog's administration section
  // opens no notification console surface, so an operator has nothing to do
  // there and a row no route reads would imply one that does not exist. It
  // takes `admin` and the revoke, and an `operator` grant skips it.
  { service: 'notification', body: 'granted-by-user-id', seeds: ['user', 'admin'] },
];

/**
 * `assets` and `search` are absent, and neither absence is an oversight.
 *
 * `assets` seeds an `operator` row and publishes **no write endpoint at all** —
 * «`assets__roles` has no write endpoint anywhere in this service, by design» —
 * so its rows arrive through the bootstrap CLI and its own §17 Q5 is open on how
 * a console-side grant should reach it. `search` seeds no `operator` at all: an
 * operator there resolves to the default role and reads what a subscriber reads.
 *
 * Naming them here rather than silently omitting them is the point — an operator
 * granted from this console cannot upload, and that is a known gap with an owner
 * rather than a surprise.
 */
export const SERVICES_WITHOUT_A_GRANT_ROUTE: readonly ServiceKey[] = ['assets', 'search'];

/**
 * `user` is the **default** role, and the default is the absence of a row.
 *
 * Every one of the seven routes documents `role_name: null` as the revoke that
 * «demotes to default … so the registry stays sparse». Submitting the literal
 * `"user"` would ask seven services to write an assignment row saying what an
 * account with no row already says, and `auth`'s own model names `null` as
 * «what the console's default option submits». One control, and its bottom value
 * is the revoke.
 */
export function wireRoleName(selected: string): string | null {
  return selected === 'user' ? null : selected;
}

/** The targets a grant of this name is actually addressed to. */
export function targetsFor(roleName: string | null): readonly GrantTarget[] {
  if (roleName === null) return GRANT_TARGETS;
  return GRANT_TARGETS.filter((target) => target.seeds.includes(roleName));
}

/**
 * One service's body, in that service's own spelling.
 *
 * `grantedByUserId` is **required**, not optional: `engagement` and `support`
 * both declare it a non-empty string, so a body without it is a `422` on two of
 * the seven. It is the signed-in operator's own id, which is why the mutation
 * refuses to start without a session rather than sending six good calls and two
 * broken ones.
 */
export function grantBody(
  target: GrantTarget,
  roleName: string | null,
  grantedByUserId: string,
): Record<string, unknown> {
  switch (target.body) {
    case 'role-only':
      return { role_name: roleName };
    case 'granted-by':
      return { role_name: roleName, granted_by: grantedByUserId };
    case 'granted-by-user-id':
      return { role_name: roleName, granted_by_user_id: grantedByUserId };
  }
}
